import useLangText from '@shared/hooks/useLangText';
import {
    ProcessFieldBase,
    type ProcessFieldSelectService as ProcessFieldSelectType,
} from '@shared/types/process';
import { getUrlSelectServicePagination } from '@shared/util/getSelectServiceOptions';

import { ProcessFieldSelectServiceFieldAiChat } from './ProcessFieldSelectServiceFieldAiChat';

import { ShadcnForm } from '@shadcn-features/core';
import { ShadcnFormSelectProps } from '@shadcn-features/core/ShadcnForm/fields';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';

interface Props extends Omit<ShadcnFormSelectProps, 'query'> {
    field: ProcessFieldBase & ProcessFieldSelectType;
    variant?: 'default' | 'ai-chat';
    aiChatShowSearch?: boolean;
}

const normalizeSelectedIds = (value: unknown): string[] => {
    if (value === undefined || value === null || value === '') return [];

    let parsedValue: unknown = value;
    if (typeof value === 'string') {
        try {
            parsedValue = JSON.parse(value) as unknown;
        } catch {
            parsedValue = value;
        }
    }

    return (Array.isArray(parsedValue) ? parsedValue : [parsedValue])
        .filter(
            (item): item is string | number =>
                typeof item === 'string' || typeof item === 'number',
        )
        .map(String)
        .filter(Boolean);
};

export function ProcessFieldSelectServiceField({
    field,
    fieldProps,
    variant = 'default',
    aiChatShowSearch,
    ...props
}: Props) {
    const getLangText = useLangText();

    const form = ShadcnForm.useFormInstance();
    const selectedValue = useWatch({
        control: form.control,
        name: props.name,
        defaultValue: form.getValues(props.name),
    });
    const defaultIds = useMemo(() => {
        const formIds = normalizeSelectedIds(selectedValue);
        if (formIds.length > 0) return formIds;

        return [
            ...normalizeSelectedIds(field.value),
            ...normalizeSelectedIds(field.defaultValue),
        ].filter((value, index, values) => values.indexOf(value) === index);
    }, [field.defaultValue, field.value, selectedValue]);

    const dependentNames = useMemo(
        () =>
            field.options.dependentCodeForParams
                ?.filter(
                    (item) =>
                        item.source === 'fromFieldId' && item.dependantFieldId,
                )
                .map((item) => item.dependantFieldId!) ?? [],
        [field.options.dependentCodeForParams],
    );
    const dependentValues = useWatch({
        control: form.control,
        name: dependentNames,
    });

    const params = useMemo(() => {
        if (!field.options.dependentCodeForParams) return;
        const res: Record<string, string[] | string> = {};

        for (const a of field.options.dependentCodeForParams) {
            if (a.paramName === 'OnlyInactive' && a.OnlyInactive) {
                res.OnlyInactive = 'true';
                continue;
            }

            if (!a.paramName && !a.source) continue;
            let value: string | string[] =
                a.source === 'manual' ? (a.dependentDictionaryIds ?? []) : '';
            if (a.source === 'fromFieldId' && a.dependantFieldId) {
                value =
                    dependentValues[
                        dependentNames.indexOf(a.dependantFieldId)
                    ] ?? '';
            }

            if (Object.prototype.hasOwnProperty.call(res, `${a.paramName}`)) {
                const existing = res[`${a.paramName}`];
                const newData = Array.isArray(value) ? value : [value];

                if (Array.isArray(existing)) {
                    res[`${a.paramName}`] = [...existing, ...newData];
                } else {
                    res[`${a.paramName}`] = [existing, ...newData];
                }
            } else {
                res[`${a.paramName}`] = value;
            }
        }

        return res;
    }, [field.options.dependentCodeForParams, dependentNames, dependentValues]);

    const query = useMemo(
        () =>
            getUrlSelectServicePagination({
                paramCode: field.options.code,
                paramType: field.options.serviceType,
                params,
                enabled: !!field.options.code,
                defaultIds: defaultIds.length ? defaultIds : undefined,
            }),
        [field.options.code, field.options.serviceType, params, defaultIds],
    );

    if (variant === 'ai-chat') {
        return (
            <ProcessFieldSelectServiceFieldAiChat
                field={field}
                fieldProps={fieldProps}
                {...props}
                query={query}
                showSearch={aiChatShowSearch ?? true}
            />
        );
    }

    if (field?.options.mode == 'multiple')
        return (
            <ShadcnForm.MultiSelectPagination
                fieldProps={{
                    ...fieldProps,
                    maxCount: field?.options.maxCount
                        ? Number(field?.options.maxCount)
                        : undefined,
                }}
                allowClear
                {...props}
                mode='multiple'
                tooltip={getLangText(field?.tooltip ?? '')}
                query={query}
            />
        );

    return (
        <>
            <ShadcnForm.SelectPagination
                fieldProps={{
                    ...fieldProps,
                }}
                allowClear
                {...props}
                tooltip={getLangText(field?.tooltip ?? '')}
                showSearch
                query={query}
            />
        </>
    );
}
